import { useRef, useState, useEffect, useContext } from "react";
import { Dropdown, Modal, Table, Button, OverlayTrigger } from "react-bootstrap";
import { Bar, Doughnut, Line } from "react-chartjs-2";
import { BiDotsHorizontalRounded, BiDownload, BiTrendingDown, BiSolidFileExport, BiTrendingUp, BiInfoCircle } from "react-icons/bi";
import { BsTable } from "react-icons/bs";
import { CgMaximize } from "react-icons/cg";
import EmptyGraphDataCard from "../../pages/SubscriberSupplierAdmin/CompanyDashboardGraph/EmptyGraphDataCard";
import Utils from "../utilities/Utils";
import * as XLSX from "xlsx";
import DashGraphConfigService from "../service/DashGraphConfigService";
import { EventContext } from "../../App";
import { EventType } from "../constants/common";
import { AiFillCloseCircle } from "react-icons/ai";
import { toJpeg } from 'html-to-image';
import { useTranslation } from "react-i18next";

// Custom Hook for Modern KPI Graph Card
function useModernKpiGraphCard(t) {
  const { setEvent } = useContext(EventContext);
  const [chartData, setChartData] = useState({ labels: [], datasets: [] });
  const [chartOptions, setChartOptions] = useState();
  const [tableViewEnabled, setTableViewEnabled] = useState(false);
  const [maximized, setMaximized] = useState(false);
  const chartRef = useRef(null);
  const chartRefExt = useRef(null);
  const now = new Date();
  const currentMonth = now.getMonth();
  const currentYear = now.getFullYear();
  const [graphTitleName, setGraphTitleName] = useState('graph_img');
  const hiddenButtonRef = useRef(null);
  const divRef = useRef();
  const { k, i18n } = useTranslation();
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [graphInfo, setGraphInfo] = useState(null);
  const getChart = (graphType, ref) => {
    switch (graphType) {
      case 'PIE':
        return <Doughnut data={chartData} options={chartOptions}
          width={"300vh"}
          height={"300vh"}
          ref={ref} />;
      case 'LINE':
        return <Line data={chartData} options={chartOptions} ref={chartRef} />;
      case 'BAR':
      case 'HORIZONTAL BAR':
      case 'HORIZONTAL STACKED BAR':
      case 'STACKED BAR':
        return <Bar data={chartData} options={chartOptions} ref={ref} />;
      default:
        return <EmptyGraphDataCard />;
    }
  };

  const onDownload = (graphTitle, ref = null) => {
    const chartInstance = ref != null ? ref?.current : chartRef.current;
    if (chartInstance) {
      const chart = chartInstance.canvas;
      const context = chart.getContext('2d');
      // Save the current canvas state
      context.save();
      context.globalCompositeOperation = 'destination-over';
      context.fillStyle = '#ffffff'; // Change to 'transparent' for a transparent background
      context.fillRect(0, 0, chart.width + 300, chart.height + 300);
      const url = chart.toDataURL('image/jpeg');
      const link = document.createElement('a');
      link.href = url;
      link.download = `${graphTitle}.jpg`;
      link.click();
      context.restore();
    }
  };

  const onDownloadMaximized = (graphTitle, ref = null) => {
    console.log('graphTitle', graphTitle);
    const chartInstance = ref != null ? ref?.current : chartRef.current;
    if (chartInstance) {
      console.log('chartInstance', chartInstance);
      const originalWidth = chartInstance.width;
      const originalHeight = chartInstance.height;

      chartInstance.resize(1200, 800);
      const chart = chartInstance.canvas;
      const context = chart.getContext('2d');
      context.save();
      context.globalCompositeOperation = 'destination-over';
      context.fillStyle = '#ffffff';
      context.fillRect(0, 0, chart.width + 300, chart.height + 300);

      const url = chart.toDataURL('image/jpeg');
      const link = document.createElement('a');
      link.href = url;
      link.download = `${graphTitle}.jpg`;
      link.click();
      // Restore canvas and chart size
      context.restore();
      chartInstance.resize(originalWidth, originalHeight);
    }
  };

  const renderMoreOptions = ({ graphType, graphTitle, id, graphConfigType, graphInfo }) => {
    console.log('graphInfo', graphInfo);
    return (
      <Dropdown>
        <Dropdown.Toggle variant="blank_outline" id="graphMoreOptions">
          <BiDotsHorizontalRounded />
        </Dropdown.Toggle>
        <Dropdown.Menu>
          <Dropdown.Item
            href="#"
            onClick={() => {
              setGraphInfo(graphInfo);
              setShowInfoModal(true);
              setMaximized(false);
              setTableViewEnabled(false);
            }} >
            <BiInfoCircle /> {t("getInfo.label")}
          </Dropdown.Item>
          {
            graphType !== 'KPI' &&
            <Dropdown.Item
              href="#"
              onClick={() => {
                setMaximized(!maximized);
                setTableViewEnabled(false);
              }} >
              <CgMaximize /> {t("Maximize.label")}
            </Dropdown.Item>
          }
          <Dropdown.Item
            href="#"
            onClick={() => {
              setMaximized(false);
              setTableViewEnabled(!tableViewEnabled);
            }} >
            <BsTable /> {t("TableView.label")}
          </Dropdown.Item>
          <Dropdown.Item
            href="#"
            onClick={() => onDownloadMaximized(graphTitle)} >
            <BiDownload /> {t("Download.label")}
          </Dropdown.Item>
          {
            graphConfigType === 'ESG' &&
            <Dropdown.Item
              href="#"
              onClick={() => onDeactivate(id)} >
              <AiFillCloseCircle /> {t("Deactivate.label")}
            </Dropdown.Item>
          }
        </Dropdown.Menu>
      </Dropdown>
    );
  };

  const renderFooter = (graph, year, config) => {
    const elements = [];
    const leftElements = [];
    const rightElements = [];;
    graph?.footers?.forEach((f, index) => {
      const uom = graph?.dataPointOptions?.find(dp => dp.label === f.label)?.unitOfMeasure ?? '';
      const dpConfig = config?.dataPoints?.find(dp => dp.label === f.label && f.position === dp.position);
      const hasIncreased = f.currentValue > f.comparedValue;
      const element = (
        <OverlayTrigger
          placement="top"
          overlay={Utils.renderTooltip(
            `${t("TotalText.label")} ${year}`
          )}>
          <div key={`footerSubEl-${index}`} id={`footerSubEl-${index}`} className="Energy_Produced_card-footer_in">
            <label>
              Total
            </label>
            <h6>{`${Utils.formattedNumber(f.value)} ${uom}`}</h6>
            {/* <b>{f.label}</b> */}
          </div>
        </OverlayTrigger>
      );
      const rightElement = (
        <OverlayTrigger
          placement="top"
          overlay={Utils.renderTooltip(
            `${t("TrendText.label")} ${year - 1}`
          )}>
          <div key={`footerSubEl-${index}`} id={`footerSubEl-${index}`} className="Energy_Produced_card-footer_in">
            {
              dpConfig.comparison && hasIncreased
                ?
                <label className={dpConfig.comparisonStyle === 'HIGHER THE BETTER' ? 'text-success' : 'text-danger'}>
                  <BiTrendingUp className="font-18" />
                </label>
                :
                <label className={dpConfig.comparisonStyle === 'HIGHER THE BETTER' ? 'text-danger' : 'text-success'}>
                  <BiTrendingDown className="font-18" />
                </label>
            }
            <h6>{`${f.value} ${uom}`}</h6>
          </div>
        </OverlayTrigger>
      );
      if (f.position === 'BOTTOM LEFT') {
        leftElements.push(element);
      } else if (f.position === 'BOTTOM RIGHT') {
        rightElements.push(rightElement);
      }
    });

    let footerCount = leftElements.length + rightElements.length;
    if (footerCount % 2 !== 0) {
      footerCount += 1;
    }
    footerCount /= 2;
    for (let index = 0; index < footerCount; index++) {
      const left = leftElements.splice(0, 1);
      const right = rightElements.splice(0, 1);
      const fclassName = graph?.type === 'KPI' ? '' : 'Energy_Produced_card-footer';
      elements.push(
        <div key={`footerEl-${index}`} id={`footerEl-${index}`}
          className={fclassName}>
          {left}{right}
        </div>
      );
    }
    return elements;
  };

  const renderDisplayModal = (type, graph, year, config) => {
    if (graph?.type === 'KPI') return <></>;

    if (!maximized && !tableViewEnabled) return <></>;

    const title = type === 'COMPANY_GRAPH'
      ? graph?.properties?.[i18n.language] || graph?.properties?.title
      : graph?.title;

    return (
      <Modal size="xl" show={true}
        className="NewDashboard_modal Goal_KpiModal"
        centered
        onHide={() => {
          setMaximized(false);
          setTableViewEnabled(false);
        }}>
        <Modal.Header className=" p-4 pb-0">
          <h5>{title}</h5>
          <div className="graph_modalBox_in">
            {/* <h6>{maximized ? 'Graph View' : 'Table View'}</h6> */}
            {maximized &&
              <OverlayTrigger
                placement="top"
                overlay={Utils.renderTooltip(
                  t("DownloadGraph.label")
                )}>
                <Button variant="outline-secondary" className="border-0 btn-sm" onClick={() => { handleCapture(title) }}>
                  <BiDownload />
                </Button>
              </OverlayTrigger>
            }
            {tableViewEnabled &&
              <OverlayTrigger
                placement="top"
                overlay={Utils.renderTooltip(
                  t("DownloadSS.label")
                )}>
                <Button variant="outline-secondary" className="border-0 btn-sm" onClick={() => download()}>
                  <BiSolidFileExport />
                </Button>
              </OverlayTrigger>
            }
            <button className="btn btn-sm btn-close border-0 me-1" aria-label="Close"
              onClick={() => {
                setMaximized(false);
                setTableViewEnabled(false);
              }}></button>
          </div>
        </Modal.Header>
        <Modal.Body className="p-2 Graph_Maximizedbox" ref={divRef}>
          <div className="p-2">
            <div className="graph_modalBox">
              <div className="graph_totalbox">
                {maximized && renderFooter(graph, year, config)}
              </div>
            </div>
            <div style={(maximized && graph.type == 'PIE') ? { height: "calc(100vh - 208px)" } : {}}>
              {maximized && getChart(graph?.type, chartRefExt)}
            </div>
            {tableViewEnabled && renderTabularData(type, graph, year)}
          </div>
        </Modal.Body>
      </Modal>
    );
  };

  const renderTabularDataForKPI = (graph) => {
    const dataPoints = graph?.legends?.sort((a, b) => a.order - b.order);
    const colHeaders = [
      ...new Set(dataPoints.flatMap((dp) => dp.yvalues.map((y) => y.label))),
    ];
    const xHeader = graph?.properties?.labels?.yaxis?.toLowerCase() ?? 'Time';
    return (
      <div className="pt-3">
        <Table bordered hover>
          <thead>
            <tr>
              <th><span className="text-caps">{xHeader}</span></th>
              {colHeaders.map((header, index) => (
                <th key={`graphTableColHeader-${index}`}>{header}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {dataPoints.map((dp) => {
              const { xvalue, yvalues } = dp;
              const valuesMap = yvalues.reduce((obj, y) => {
                obj[y.label] = y.value;
                return obj;
              }, {});
              return (
                <tr key={`graphTableCell-${dp.order}`}>
                  <td>{xvalue}</td>
                  {colHeaders.map((header, index) => (
                    <td key={`graphTableCell-${dp.order}-${index}`}>{valuesMap[header] ?? 0} {graph?.uom ?? ''}</td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </Table>
      </div>
    );
  };

  const getLocalizedLabel = (localizedContents, languageCode, fieldName) => {
    if (!Array.isArray(localizedContents)) return null;
    const localized = localizedContents.find(
      (content) => content.languageCode === languageCode && content.fieldName === fieldName
    );
    return localized ? localized.translatedValue : null;
  };

  const renderTabularData = (type, graph, year) => {
    if (type === 'KPI_GRAPH') return renderTabularDataForKPI(graph);
    if (year == currentYear) {
      if (currentMonth < 11) {
        graph.dataPoints.splice(currentMonth + 1);
      }
    }
    const dataPoints = graph?.dataPoints?.sort((a, b) => a.order - b.order);
    const uomMap = graph?.dataPointOptions?.reduce((obj, y) => {
      obj[y.label] = y.unitOfMeasure;
      return obj;
    }, {});
    if (graph?.type === 'PIE') {
      return (
        <div className="pt-3">
          <Table bordered hover>
            <thead>
            </thead>
            <tbody>
              {dataPoints[0]?.yValues?.map((dp) => {
                const localizedLabel = getLocalizedLabel(dp.localizedContents, i18n.language, 'label');
                return (
                  <tr key={`graphTableCell-${dp.order}`}>
                    <td>{localizedLabel ?? dp.label}</td>
                    <td>{dp.value} {uomMap[dp.label] ?? ''}</td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </div>
      );
    }


    const colHeaders = [
      ...new Set(
        dataPoints.flatMap((dp) =>
          dp.yValues.map((y) =>
            getLocalizedLabel(y.localizedContents, i18n.language, 'label') ?? y.label
          )
        )
      ),
    ];
    const capitalizeFirstLetter = (str) => {
      if (!str) return str;
      return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
    };

    const xAxisKey = `${capitalizeFirstLetter(graph?.properties?.labels?.xAxis)}.label`;

    const xHeader =
      getLocalizedLabel(graph?.properties?.labels?.localizedContents, i18n.language, 'xAxis') ??
      i18n.t(xAxisKey) ??
      'Time';

    // const xHeader =
    //   getLocalizedLabel(graph?.properties?.labels?.localizedContents, i18n.language, 'xAxis') ??
    //   graph?.properties?.labels?.xAxis.toLowerCase() ??
    //   'Time';

    return (
      <div className="pt-3">
        <Table bordered hover>
          <thead>
            <tr>
              <th><span className="text-caps">{xHeader}</span></th>
              {colHeaders.map((header, index) => (
                <th key={`graphTableColHeader-${index}`}>{header}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {dataPoints.map((dp) => {
              const { xValue, yValues } = dp;
              // Create a map of original labels to their corresponding values
              const labelToOriginalMap = yValues.reduce((obj, y) => {
                const translatedLabel = getLocalizedLabel(y.localizedContents, i18n.language, 'label') ?? y.label;
                obj[translatedLabel] = y;
                return obj;
              }, {});

              return (
                <tr key={`graphTableCell-${dp.order}`}>
                  <td>{i18n.t(xValue)}</td>

                  {/* <td> {i18n.t(`${xValue}.label`)}</td> */}
                  {/* <td>
                    {xValue}</td> */}
                  {colHeaders.map((header, index) => {
                    // Find the corresponding original label for this translated header
                    const originalY = labelToOriginalMap[header];  // Get the full object by translated label

                    // If a match is found, use the original label for UOM lookup
                    const originalLabel = originalY ? originalY.label : '';
                    const uom = uomMap[originalLabel] ?? '';  // Lookup UOM using the original label

                    return (
                      <td key={`graphTableCell-${dp.order}-${index}`}>
                        {Utils.formattedNumber(originalY ? originalY.value : 0)} {uom}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </Table>

        <button onClick={() => exportToExcel("TableData", xHeader, colHeaders, dataPoints, uomMap)} ref={hiddenButtonRef} style={{ display: 'none' }}></button>
      </div>
    );
  };

  const exportToExcel = (fileName, xHeader, colHeaders, dataPoints, uomMap) => {

    const headers = [xHeader, ...colHeaders.map((header) => `${header} (${uomMap[header] || ''})`)];

    const rows = dataPoints.map((dp) => {
      const { xValue, yValues } = dp;
      const valuesMap = yValues.reduce((obj, y) => {
        obj[y.label] = y.value;
        return obj;
      }, {});
      return [xValue, ...colHeaders.map((header) => valuesMap[header] || 0)];
    });

    const worksheetData = [headers, ...rows];

    const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Data");

    XLSX.writeFile(workbook, `${fileName}.xlsx`);
  };

  const download = () => {
    if (hiddenButtonRef.current) {
      hiddenButtonRef.current.click();
    }
  }

  const onDeactivate = (id) => {
    DashGraphConfigService.activateGraph(id, false).then(res => {
      if (res.status !== 200) return;

      setEvent({
        event: EventType.REFRESH_COMPANY_GRAPHS,
        data: id
      })
    }).catch(err => { });
  };

  const handleCapture = (title) => {
    if (divRef.current) {
      toJpeg(divRef.current, {
        backgroundColor: 'white', // Set white background
        quality: 2.0,             // Set JPEG quality to maximum
        pixelRatio: 3,            // Increase pixel density (e.g., 2x for higher clarity)
      })
        .then((dataUrl) => {
          // Create a download link or display the image
          const link = document.createElement('a');
          link.href = dataUrl;
          link.download = (title + '.jpg').trimStart();
          link.click();
        })
        .catch((err) => {
          console.error('Something went wrong!', err);
        });
    }
  };

  const handleCloseInfoModal = () => {
    setShowInfoModal(false);
    setGraphInfo(null);
  }

  const renderInfoModal = () => {
    return (
      <>
        {
          showInfoModal &&
          <Modal
            className='NewDashboard_modal chart-info-modal'
            show={showInfoModal}
            onHide={handleCloseInfoModal}
            aria-labelledby="chart-info-modal-title 1"
            centered
          >
            <Modal.Body>
              <div className="chart-info-content px-4 py-4">
                <div className="text-end">
                  <button type="button" className="btn btn-sm btn-close border-0 ms-2" aria-label="Close"
                    onClick={handleCloseInfoModal}></button>
                </div>
                {graphInfo &&
                  <div>
                    <strong>{t("chartInfo.description.label")}:</strong>
                    <p className="description-text">{graphInfo}</p>
                  </div>
                }
              </div>
            </Modal.Body>
          </Modal>
        }
      </>
    );
  };

  return {
    //Functions
    renderMoreOptions,
    getChart,
    renderDisplayModal,
    renderInfoModal,
    onDeactivate,
    // States
    chartRef,
    chartData, setChartData,
    chartOptions, setChartOptions,
    tableViewEnabled, setTableViewEnabled,
    maximized, setMaximized,
    showInfoModal, setShowInfoModal,
  };
}


export default useModernKpiGraphCard; 